/**
 * O Desk Cloud (docs/spec/desk-cloud.md): os arquivos de cada conta, com tipo de RPG, pastas numa
 * árvore, favoritos, lixeira e as mesas a que cada arquivo está ligado. Aqui ficam os tipos que o
 * servidor e o cliente trocam, os limites, o tipo deduzido no envio e as contas da árvore de pastas.
 * O servidor confere tudo; a tela usa o mesmo para mostrar e avisar antes.
 */
import { z } from 'zod';
import { IdSchema } from './domain.js';

const DAY = 24 * 60 * 60 * 1000;

export const DESK_CLOUD_LIMITS = {
  /** arquivos por página da listagem */
  pageSize: 100,
  /** ids por chamada (as ações em massa, as ligações, o zip) */
  idsPerCall: 500,
  /** mesas de uma vez em `POST /api/files/rooms` */
  roomsPerCall: 50,
  folderName: 80,
  foldersPerAccount: 1000,
  /** níveis da árvore: uma pasta na raiz está no nível 1 */
  folderDepth: 8,
  /** dias na lixeira antes de sair de vez */
  trashDays: 30,
  trashMs: 30 * DAY,
  /** quantos arquivos a visão Recentes mostra */
  recent: 60,
  /** a busca, no nome */
  query: 100,
  /** a miniatura: o maior lado, o maior tamanho e o menor arquivo que ganha miniatura */
  thumbSide: 320,
  thumbMaxBytes: 200_000,
  thumbMinFileBytes: 64_000,
  /** o zip: arquivos, bytes e por quanto tempo a ficha vale */
  zipMaxFiles: 500,
  zipMaxBytes: 2 * 1024 * 1024 * 1024,
  zipTtlMs: 5 * 60 * 1000,
  /** os metadados que o envio informa (só para mostrar) */
  maxDimension: 100_000,
  maxDurationMs: 24 * 60 * 60 * 1000,
  /** arquivos do painel da mesa */
  roomPanel: 300,
} as const;

/** Os tipos de RPG, na ordem dos chips. */
export const FILE_KINDS = ['map', 'token', 'audio', 'doc', 'other'] as const;
export const FileKindSchema = z.enum(FILE_KINDS);
export type FileKind = z.infer<typeof FileKindSchema>;

/**
 * Onde o arquivo entra no espaço (a barra do armazenamento): os tipos, mais as fotos de perfil e de
 * membro, que ficam fora das listas.
 */
export const STORAGE_BUCKETS = [...FILE_KINDS, 'photo'] as const;
export type StorageBucket = (typeof STORAGE_BUCKETS)[number];

/** As visões da lateral. `room` e `folder` pedem o id. */
export const FILE_VIEWS = ['all', 'recent', 'favorites', 'trash', 'unlinked', 'room', 'folder'] as const;
export const FileViewSchema = z.enum(FILE_VIEWS);
export type FileView = z.infer<typeof FileViewSchema>;

export const FILE_SORTS = ['recent', 'name', 'size'] as const;
export const FileSortSchema = z.enum(FILE_SORTS);
export type FileSort = z.infer<typeof FileSortSchema>;

/**
 * Um arquivo do Desk Cloud, como o servidor entrega.
 *
 * `url` e `thumbUrl` são links temporários assinados, calculados na hora de listar: é assim que um
 * `<img>`/`<audio>` busca o arquivo sem que o token da sessão apareça na URL. Para quem não é o dono
 * (o painel da mesa), a pasta, o favorito e as outras mesas não vão.
 */
export const DeskFileSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(200),
  mime: z.string().max(120),
  size: z.number().int().nonnegative(),
  kind: FileKindSchema,
  /** quem enviou (só o dono renomeia, move, liga a mesas ou apaga) */
  ownerId: IdSchema,
  folderId: IdSchema.nullable(),
  favorite: z.boolean(),
  /** na lixeira desde (ms); null fora dela */
  trashedAt: z.number().int().nullable(),
  /** as mesas a que está ligado: quem frequenta uma delas pode abrir */
  rooms: z.array(IdSchema),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
  createdAt: z.number().int().nonnegative(),
  usedAt: z.number().int().nonnegative().nullable(),
  url: z.string(),
  thumbUrl: z.string().nullable(),
});
export type DeskFile = z.infer<typeof DeskFileSchema>;

/** Uma pasta, com os arquivos dela (sem os das subpastas e sem a lixeira). */
export const DeskFolderSchema = z.object({
  id: IdSchema,
  parentId: IdSchema.nullable(),
  name: z.string().min(1).max(DESK_CLOUD_LIMITS.folderName),
  files: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
  createdAt: z.number().int().nonnegative(),
});
export type DeskFolder = z.infer<typeof DeskFolderSchema>;

/** Uma mesa da lateral: as que têm arquivos meus e as que eu mestro. */
export const DeskRoomSchema = z.object({
  id: IdSchema,
  name: z.string(),
  /** meus arquivos ligados a ela, fora da lixeira */
  files: z.number().int().nonnegative(),
  /** quem já entrou nela, fora eu (os que podem abrir os arquivos ligados) */
  members: z.number().int().nonnegative(),
  /** sou dono ou mestre auxiliar: posso ligar arquivos a ela */
  gm: z.boolean(),
});
export type DeskRoom = z.infer<typeof DeskRoomSchema>;

export const DeskCloudSummarySchema = z.object({
  usage: z.object({
    used: z.number().int().nonnegative(),
    quota: z.number().int().nonnegative(),
    /** o que está na lixeira (já está em `used`) */
    trash: z.number().int().nonnegative(),
    /** fora da lixeira, por tipo, mais as fotos */
    byKind: z.record(z.enum(STORAGE_BUCKETS), z.number().int().nonnegative()),
  }),
  views: z.object({
    all: z.number().int().nonnegative(),
    recent: z.number().int().nonnegative(),
    favorites: z.number().int().nonnegative(),
    trash: z.number().int().nonnegative(),
    unlinked: z.number().int().nonnegative(),
  }),
  rooms: z.array(DeskRoomSchema),
  folders: z.array(DeskFolderSchema),
});
export type DeskCloudSummary = z.infer<typeof DeskCloudSummarySchema>;

/** Uma página da listagem, com os números por tipo da visão (os chips), sem o filtro de tipo. */
export const DeskFilePageSchema = z.object({
  files: z.array(DeskFileSchema),
  total: z.number().int().nonnegative(),
  kinds: z.record(FileKindSchema, z.number().int().nonnegative()),
});
export type DeskFilePage = z.infer<typeof DeskFilePageSchema>;

/* ------------------------------------------------------------------ corpos das rotas */

const IdsSchema = z.array(IdSchema).min(1).max(DESK_CLOUD_LIMITS.idsPerCall);
export const FolderNameSchema = z.string().trim().min(1).max(DESK_CLOUD_LIMITS.folderName);
export const FileNameSchema = z.string().trim().min(1).max(200);

/** `PATCH /api/files/:id` */
export const FilePatchSchema = z
  .object({
    name: FileNameSchema.optional(),
    kind: FileKindSchema.optional(),
    folderId: IdSchema.nullable().optional(),
    favorite: z.boolean().optional(),
  })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, 'nada para mudar');
export type FilePatch = z.infer<typeof FilePatchSchema>;

/** `POST /api/files/bulk` */
export const FileBulkSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('move'), ids: IdsSchema, folderId: IdSchema.nullable() }).strict(),
  z.object({ action: z.literal('favorite'), ids: IdsSchema }).strict(),
  z.object({ action: z.literal('unfavorite'), ids: IdsSchema }).strict(),
  z.object({ action: z.literal('kind'), ids: IdsSchema, kind: FileKindSchema }).strict(),
  z.object({ action: z.literal('trash'), ids: IdsSchema }).strict(),
  z.object({ action: z.literal('restore'), ids: IdsSchema }).strict(),
  z.object({ action: z.literal('purge'), ids: IdsSchema }).strict(),
  z.object({ action: z.literal('emptyTrash') }).strict(),
]);
export type FileBulk = z.infer<typeof FileBulkSchema>;

const RoomIdsSchema = z.array(IdSchema).max(DESK_CLOUD_LIMITS.roomsPerCall);

/** `POST /api/files/rooms`: liga e desliga de uma vez (a seleção pode ser mista). */
export const FileRoomsSchema = z
  .object({ ids: IdsSchema, add: RoomIdsSchema.default([]), remove: RoomIdsSchema.default([]) })
  .strict()
  .refine((body) => body.add.length + body.remove.length > 0, 'nada para mudar');
export type FileRooms = z.infer<typeof FileRoomsSchema>;

/** `POST /api/files/folders` */
export const FolderCreateSchema = z.object({ name: FolderNameSchema, parentId: IdSchema.nullable() }).strict();
/** `PATCH /api/files/folders/:id` */
export const FolderPatchSchema = z
  .object({ name: FolderNameSchema.optional(), parentId: IdSchema.nullable().optional() })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, 'nada para mudar');

/** `POST /api/files/zip`: uma seleção, uma pasta (com a árvore) ou os meus ligados a uma mesa. */
export const FileZipSchema = z.union([
  z.object({ ids: IdsSchema }).strict(),
  z.object({ folderId: IdSchema }).strict(),
  z.object({ roomId: IdSchema }).strict(),
]);
export type FileZip = z.infer<typeof FileZipSchema>;

/* ------------------------------------------------------------------ a referência a um arquivo */

/**
 * Um arquivo do Desk Cloud guardado onde cabe um endereço (a capa da mesa, a imagem de um plugin):
 * `rpgdesk-file:<id>`. O link de verdade é pedido na hora de mostrar, porque os assinados expiram.
 */
export const DESK_FILE_REF = 'rpgdesk-file:';
const DESK_FILE_REF_RE = /^rpgdesk-file:([A-Za-z0-9_-]{1,64})$/;

/** O id do arquivo de uma referência `rpgdesk-file:<id>`, ou null. */
export function deskFileRefId(value: unknown): string | null {
  return typeof value === 'string' ? (DESK_FILE_REF_RE.exec(value)?.[1] ?? null) : null;
}

/* ------------------------------------------------------------------ o tipo no envio */

/** O lugar do envio, que decide o tipo antes do mime: soltar na cena, o fundo, a música. */
export type KindOrigin = 'token' | 'map' | 'audio';

const AUDIO_EXT = /\.(mp3|ogg|oga|opus|wav|m4a|aac|flac|weba)$/i;
const DOC_EXT = /\.(pdf|txt|md|markdown|rtf|docx?|odt)$/i;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|bmp|svg)$/i;

/** O maior lado de um token, e a proporção (largura ÷ altura) que ainda conta como quase quadrada. */
const TOKEN_MAX_SIDE = 1024;
const TOKEN_RATIO: readonly [number, number] = [0.75, 1.33];

/**
 * O tipo de um arquivo novo. O lugar do envio vale primeiro, quando combina com o arquivo (soltar uma
 * imagem na cena é token; uma música é áudio); senão o mime e a extensão; uma imagem pequena e quase
 * quadrada é token, as outras são mapa.
 */
export function inferKind(file: { mime: string; name: string; width?: number | null; height?: number | null }, origin?: KindOrigin): FileKind {
  const mime = file.mime.toLowerCase();
  // sem um mime de verdade (o navegador não soube), vale a extensão
  const generic = !mime || mime === 'application/octet-stream';
  const audio = mime.startsWith('audio/') || (generic && AUDIO_EXT.test(file.name));
  const image = mime.startsWith('image/') || (generic && IMAGE_EXT.test(file.name));
  if (origin === 'audio' && audio) return 'audio';
  if ((origin === 'token' || origin === 'map') && image) return origin;
  if (audio) return 'audio';
  if (mime === 'application/pdf' || mime.startsWith('text/') || DOC_EXT.test(file.name)) return 'doc';
  if (image) {
    const { width, height } = file;
    if (width && height) {
      const ratio = width / height;
      if (Math.max(width, height) <= TOKEN_MAX_SIDE && ratio >= TOKEN_RATIO[0] && ratio <= TOKEN_RATIO[1]) return 'token';
    }
    return 'map';
  }
  return 'other';
}

/* ------------------------------------------------------------------ a árvore de pastas */

type Node = { id: string; parentId: string | null };

/** A pasta e as mães dela, da raiz até ela (vazio se não existe). Para num ciclo, se houver. */
export function folderChain<T extends Node>(folders: readonly T[], id: string | null): T[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const chain: T[] = [];
  const seen = new Set<string>();
  for (let at = id ? byId.get(id) : undefined; at && !seen.has(at.id); at = at.parentId ? byId.get(at.parentId) : undefined) {
    seen.add(at.id);
    chain.unshift(at);
  }
  return chain;
}

/** O nível da pasta: 1 na raiz. */
export const folderDepth = (folders: readonly Node[], id: string): number => folderChain(folders, id).length;

/** A pasta e todas as de dentro dela. */
export function folderSubtree(folders: readonly Node[], id: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const f of folders) if (f.parentId) children.set(f.parentId, [...(children.get(f.parentId) ?? []), f.id]);
  const out = new Set<string>();
  const stack = [id];
  for (let at = stack.pop(); at !== undefined; at = stack.pop()) {
    if (out.has(at)) continue;
    out.add(at);
    stack.push(...(children.get(at) ?? []));
  }
  return out;
}

/** Quantos níveis a pasta ocupa com as de dentro: 1 para uma pasta sem filhas. */
export function folderHeight(folders: readonly Node[], id: string): number {
  const inside = folderSubtree(folders, id);
  let height = 1;
  for (const other of inside) {
    const chain = folderChain(folders, other);
    const from = chain.findIndex((f) => f.id === id);
    if (from >= 0) height = Math.max(height, chain.length - from);
  }
  return height;
}

/**
 * Por que a pasta não pode ir para dentro de `parentId` (null é a raiz): para dentro dela mesma ou de
 * uma filha (`cycle`), ou a árvore passaria dos níveis (`depth`). Null se pode.
 */
export function folderMoveProblem(folders: readonly Node[], id: string, parentId: string | null): 'cycle' | 'depth' | null {
  if (parentId === null) return folderHeight(folders, id) > DESK_CLOUD_LIMITS.folderDepth ? 'depth' : null;
  if (folderSubtree(folders, id).has(parentId)) return 'cycle';
  return folderDepth(folders, parentId) + folderHeight(folders, id) > DESK_CLOUD_LIMITS.folderDepth ? 'depth' : null;
}

/** Nome sem diferença de maiúsculas nem de acento, para conferir repetidos na mesma pasta. */
export const sameNameKey = (name: string): string => name.trim().normalize('NFC').toLocaleLowerCase('pt-BR');

/* ------------------------------------------------------------------ o zip */

/** Um nome que serve de caminho dentro do zip: sem barras, sem caracteres de controle, sem ponto no fim. */
export function zipSafeName(name: string): string {
  // eslint-disable-next-line no-control-regex
  const clean = name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').replace(/^[.\s]+|[.\s]+$/g, '');
  return clean.slice(0, 200) || '_';
}

/** "nome.ext", e na repetição "nome (2).ext", "nome (3).ext"… (`taken` guarda os já usados, sem maiúsculas). */
export function dedupeName(name: string, taken: Set<string>): string {
  const dot = name.lastIndexOf('.');
  const [stem, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
  let candidate = name;
  for (let n = 2; taken.has(candidate.toLocaleLowerCase()); n++) candidate = `${stem} (${n})${ext}`;
  taken.add(candidate.toLocaleLowerCase());
  return candidate;
}
